import { getMessaging, getToken, onMessage } from "firebase/messaging";
import {
  doc,
  setDoc,
  getDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import firestoreInstance from "./firestoreInstance";
import firebaseApp from "./firebaseApp";
import { UserNotificationPreferences, FCMToken } from "../types/Notifications";
import { firebaseMessagingConfig } from "../config/firebase-messaging";
import {
  areNotificationsSupported,
  getBrowserLimitations,
  getDetailedBrowserInfo,
  isSamsungInternet,
  isAndroid13Plus,
} from "../utils/browserDetection";

class NotificationService {
  private messaging: any = null;

  // Cache pour éviter les notifications en double côté client
  private notificationCache = new Map<string, number>();

  /**
   * Vérifie si une notification a déjà été traitée
   */
  private isNotificationAlreadyProcessed(notificationId: string): boolean {
    const now = Date.now();
    const cacheEntry = this.notificationCache.get(notificationId);

    if (cacheEntry) {
      // Si l'entrée a moins de 30 secondes, on considère que c'est un doublon
      if (now - cacheEntry < 30000) {
        return true;
      }
    }

    // Ajouter au cache
    this.notificationCache.set(notificationId, now);

    // Nettoyer le cache (garder seulement les entrées de moins de 5 minutes)
    Array.from(this.notificationCache.entries()).forEach(([key, timestamp]) => {
      if (now - timestamp > 300000) {
        this.notificationCache.delete(key);
      }
    });

    return false;
  }

  // Initialiser Firebase Messaging de manière lazy
  private getMessaging() {
    if (!this.messaging) {
      try {
        // Vérifier que Firebase est initialisé
        if (!firebaseApp) {
          console.error("🔔 [ERROR] Firebase App non initialisé");
          return null;
        }

        this.messaging = getMessaging(firebaseApp);
        console.log("🔔 [DEBUG] Firebase Messaging initialisé avec succès");
      } catch (error) {
        console.error(
          "🔔 [ERROR] Erreur lors de l'initialisation de Firebase Messaging:",
          error
        );
        return null;
      }
    }
    return this.messaging;
  }

  // Vérifier si les notifications sont supportées
  private isNotificationSupported(): boolean {
    return areNotificationsSupported();
  }

  // Initialiser les notifications
  async initializeNotifications(userId: string): Promise<string | null> {
    try {
      console.log(
        "🔔 [DEBUG] initializeNotifications - début pour l'utilisateur:",
        userId
      );

      // Vérifier si les notifications sont supportées
      if (!this.isNotificationSupported()) {
        console.log("🔔 Notifications non supportées sur cette plateforme");
        console.log("🔔 Limitations:", getBrowserLimitations());
        return null;
      }

      // Vérifier si la permission est déjà accordée
      if (Notification.permission === "granted") {
        console.log("🔔 Permission déjà accordée, obtention du token FCM...");
        const token = await this.getFCMToken(userId);
        console.log(
          "🔔 [DEBUG] initializeNotifications - Token obtenu:",
          !!token
        );
        return token;
      }

      // Si la permission n'est pas accordée, on ne peut pas la demander automatiquement
      // L'utilisateur devra cliquer sur un bouton pour l'initier
      if (Notification.permission === "default") {
        console.log(
          "🔔 Permission non demandée - nécessite une action utilisateur"
        );
        return null;
      }

      // Si la permission est refusée
      if (Notification.permission === "denied") {
        console.log("🔔 Permission de notification refusée");
        return null;
      }

      return null;
    } catch (error) {
      console.error(
        "🔔 Erreur lors de l'initialisation des notifications:",
        error
      );
      return null;
    }
  }

  // Demander la permission et obtenir le token FCM (doit être appelé depuis une action utilisateur)
  async requestPermissionAndInitialize(userId: string): Promise<string | null> {
    try {
      console.log("🔔 [DEBUG] requestPermissionAndInitialize - début");

      // Vérifier si les notifications sont supportées
      if (!this.isNotificationSupported()) {
        console.log(
          "🔔 [DEBUG] Notifications non supportées sur cette plateforme"
        );
        console.log("🔔 [DEBUG] Limitations:", getBrowserLimitations());
        return null;
      }

      console.log(
        "🔔 [DEBUG] Notifications supportées, demande de permission..."
      );

      // Demander la permission (doit être dans le contexte d'une action utilisateur)
      const permission = await Notification.requestPermission();
      console.log("🔔 [DEBUG] Permission demandée, résultat:", permission);

      if (permission !== "granted") {
        console.log("🔔 [DEBUG] Permission de notification refusée");
        return null;
      }

      console.log("🔔 [DEBUG] Permission accordée, obtention du token FCM...");

      // Obtenir le token FCM
      const token = await this.getFCMToken(userId);
      console.log("🔔 [DEBUG] Token FCM obtenu:", !!token);

      return token;
    } catch (error) {
      console.error(
        "🔔 [DEBUG] Erreur lors de la demande de permission:",
        error
      );
      return null;
    }
  }

  // Obtenir le token FCM (méthode séparée)
  private async getFCMToken(userId: string): Promise<string | null> {
    try {
      console.log("🔔 [DEBUG] getFCMToken - début");
      console.log(
        "🔔 [DEBUG] VAPID Key disponible:",
        !!firebaseMessagingConfig.vapidKey
      );

      // Informations détaillées du navigateur
      const browserInfo = getDetailedBrowserInfo();
      console.log("🔔 [DEBUG] Informations navigateur:", browserInfo);
      console.log("🔔 [DEBUG] Samsung Internet:", browserInfo.isSamsung);
      console.log("🔔 [DEBUG] Android 13+:", browserInfo.isAndroid13Plus);

      console.log("🔔 [DEBUG] Permission actuelle:", Notification.permission);

      // Vérifications spécifiques pour Samsung Internet et Android 13
      if (browserInfo.isSamsung && browserInfo.isAndroid13Plus) {
        console.log("🔔 [DEBUG] Détection: Samsung Internet sur Android 13+");
        console.log("🔔 [DEBUG] Vérification des permissions spéciales...");

        // Vérifier si les notifications sont activées dans les paramètres système
        if (Notification.permission === "denied") {
          console.error("🔔 [ERROR] Notifications refusées par l'utilisateur");
          throw new Error(
            "Les notifications ont été refusées. Veuillez les activer dans les paramètres du navigateur."
          );
        }

        // Vérifier si le service worker est disponible
        if (!("serviceWorker" in navigator)) {
          console.error(
            "🔔 [ERROR] Service Worker non supporté sur ce navigateur"
          );
          throw new Error(
            "Ce navigateur ne supporte pas les notifications push."
          );
        }
      }

      if (!firebaseMessagingConfig.vapidKey) {
        console.error(
          "🔔 [ERROR] Clé VAPID manquante ! Ajoutez REACT_APP_FIREBASE_VAPID_KEY dans votre fichier .env"
        );
        throw new Error(
          "Clé VAPID manquante. Vérifiez la configuration Firebase."
        );
      }

      const messaging = this.getMessaging();
      if (!messaging) {
        console.error("🔔 [ERROR] Firebase Messaging non initialisé");
        return null;
      }

      console.log("🔔 [DEBUG] Tentative d'obtention du token FCM...");

      const token = await getToken(messaging, {
        vapidKey: firebaseMessagingConfig.vapidKey,
      });

      console.log("🔔 [DEBUG] Token obtenu de Firebase:", !!token);
      if (token) {
        console.log(
          "🔔 [DEBUG] Token FCM (premiers caractères):",
          token.substring(0, 20) + "..."
        );
      }

      if (token) {
        console.log("🔔 [DEBUG] Sauvegarde du token FCM...");
        // Sauvegarder le token
        await this.saveFCMToken(userId, token);

        console.log(
          "🔔 [DEBUG] Configuration de l'écouteur en premier plan..."
        );
        // Écouter les messages en premier plan
        this.setupForegroundListener();

        return token;
      }

      console.log("🔔 [DEBUG] Aucun token obtenu de Firebase");
      console.log("🔔 [DEBUG] Vérification du service worker...");

      // Vérifier le service worker
      if ("serviceWorker" in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          console.log("🔔 [DEBUG] Service Worker enregistré:", !!registration);
          if (registration) {
            console.log(
              "🔔 [DEBUG] Service Worker actif:",
              registration.active?.state
            );
            console.log("🔔 [DEBUG] Service Worker scope:", registration.scope);
          }
        } catch (swError) {
          console.error("🔔 [DEBUG] Erreur Service Worker:", swError);
        }
      } else {
        console.log("🔔 [DEBUG] Service Worker non supporté");
      }

      return null;
    } catch (error) {
      console.error(
        "🔔 [DEBUG] Erreur lors de l'obtention du token FCM:",
        error
      );
      if (error instanceof Error) {
        console.error("🔔 [DEBUG] Type d'erreur:", error.constructor.name);
        console.error("🔔 [DEBUG] Message d'erreur:", error.message);
        console.error("🔔 [DEBUG] Stack trace:", error.stack);
      }
      return null;
    }
  }

  // Sauvegarder le token FCM
  private async saveFCMToken(userId: string, token: string): Promise<void> {
    const tokenRef = doc(firestoreInstance, "fcmTokens", userId);

    const tokenData: FCMToken = {
      userId,
      token,
      platform: "web",
      createdAt: new Date(),
      lastUsed: new Date(),
    };

    await setDoc(tokenRef, tokenData, { merge: true }); // ✅ utilise setDoc avec merge pour éviter d'écraser
  }

  // Configuration de l'écouteur en premier plan
  private setupForegroundListener() {
    try {
      const messaging = this.getMessaging();
      if (!messaging) {
        console.error("🔔 [ERROR] Firebase Messaging non initialisé");
        return;
      }

      onMessage(messaging, (payload) => {
        console.log("🔔 [DEBUG] Message reçu en premier plan:", payload);

        // Vérifier si c'est une notification de match
        if (payload.data?.type && payload.data?.matchId) {
          const notificationId =
            payload.data.notificationId ||
            `${payload.data.matchId}_${Date.now()}`;

          // Vérifier si cette notification a déjà été traitée
          if (this.isNotificationAlreadyProcessed(notificationId)) {
            console.log(
              "🔔 [DEBUG] Notification déjà traitée, ignorée:",
              notificationId
            );
            return;
          }

          console.log(
            "🔔 [DEBUG] Nouvelle notification de match traitée:",
            notificationId
          );
        }

        // Afficher la notification
        if (payload.notification) {
          this.showNativeNotification(
            payload.notification.title || "Nouvelle notification",
            payload.notification.body || ""
          );
        }
      });
    } catch (error) {
      console.error(
        "🔔 [ERROR] Erreur lors de la configuration de l'écouteur en premier plan:",
        error
      );
    }
  }

  // Afficher une notification native
  private showNativeNotification(title: string, body: string): void {
    try {
      // Vérifier si les notifications sont supportées
      if (!this.isNotificationSupported()) {
        console.log(
          "🔔 Impossible d'afficher la notification - plateforme non supportée"
        );
        return;
      }

      // Vérifier que les notifications sont autorisées
      if (Notification.permission !== "granted") {
        console.log("🔔 Permission de notification non accordée");
        return;
      }

      // Vérifier que les APIs nécessaires sont disponibles
      if (!("serviceWorker" in navigator)) {
        console.log("🔔 Navigateur non supporté pour les notifications");
        return;
      }

      navigator.serviceWorker.ready
        .then((registration) => {
          registration.showNotification(title, {
            body,
            icon: "/logo192.png",
            badge: "/logo192.png",
          });
        })
        .catch((error) => {
          console.error(
            "🔔 Erreur lors de l'affichage de la notification:",
            error
          );
        });
    } catch (error) {
      console.error("🔔 Erreur lors de l'affichage de la notification:", error);
    }
  }

  // Sauvegarder les préférences de notification
  async saveNotificationPreferences(
    userId: string,
    preferences: Partial<UserNotificationPreferences>
  ): Promise<void> {
    try {
      console.log(
        "🔔 [DEBUG] Sauvegarde des préférences pour l'utilisateur:",
        userId
      );
      console.log("🔔 [DEBUG] Préférences à sauvegarder:", preferences);

      const prefsRef = doc(
        firestoreInstance,
        "userNotificationPreferences",
        userId
      );

      const prefsData: Partial<UserNotificationPreferences> = {
        ...preferences,
        userId,
        updatedAt: new Date(),
      };

      // Si fcmToken n'est pas fourni dans les préférences, on le supprime explicitement
      if (!("fcmToken" in preferences)) {
        console.log(
          "🔔 [DEBUG] Suppression du token FCM des préférences (champ non fourni)"
        );
        delete prefsData.fcmToken;
      } else if (preferences.fcmToken === undefined) {
        console.log(
          "🔔 [DEBUG] Suppression du token FCM des préférences (valeur undefined)"
        );
        delete prefsData.fcmToken;
      }

      // Utiliser setDoc avec merge pour éviter d'écraser les autres champs
      await setDoc(prefsRef, prefsData, { merge: true });
      console.log("🔔 [DEBUG] Préférences sauvegardées avec succès");
    } catch (error) {
      console.error(
        "🔔 [ERROR] Erreur lors de la sauvegarde des préférences:",
        error
      );
      throw error;
    }
  }

  // Récupérer les préférences de notification
  async getNotificationPreferences(
    userId: string
  ): Promise<UserNotificationPreferences | null> {
    try {
      console.log(
        "🔔 [DEBUG] Récupération des préférences pour l'utilisateur:",
        userId
      );

      const prefsRef = doc(
        firestoreInstance,
        "userNotificationPreferences",
        userId
      );
      const prefsDoc = await getDoc(prefsRef);

      let preferences: UserNotificationPreferences | null = null;

      if (prefsDoc.exists()) {
        preferences = prefsDoc.data() as UserNotificationPreferences;
        console.log("🔔 [DEBUG] Préférences trouvées:", !!preferences);
      } else {
        console.log(
          "🔔 [DEBUG] Aucune préférence trouvée, création d'un document par défaut"
        );
        // Créer des préférences par défaut (sans token FCM)
        preferences = {
          userId,
          followedPlayers: [],
          notificationTypes: {
            matchCreated: true,
            matchCreatedBlocked: true,
            matchBlocked: true,
            matchUnblocked: true,
            matchStarting: true,
          },
          emailNotifications: {
            enabled: false,
            emails: [],
          },
          createdAt: new Date(),
          updatedAt: new Date(),
          // Pas de fcmToken par défaut - l'utilisateur doit l'activer explicitement
        };

        // Sauvegarder les préférences par défaut
        await this.saveNotificationPreferences(userId, preferences);
      }

      // Vérifier s'il y a un token FCM dans la collection fcmTokens
      const tokenRef = doc(firestoreInstance, "fcmTokens", userId);
      const tokenDoc = await getDoc(tokenRef);

      if (tokenDoc.exists()) {
        const tokenData = tokenDoc.data();
        console.log("🔔 [DEBUG] Token FCM trouvé dans la collection fcmTokens");

        // Si le token dans fcmTokens est différent de celui dans les préférences,
        // on met à jour les préférences
        if (!preferences.fcmToken || preferences.fcmToken !== tokenData.token) {
          console.log(
            "🔔 [DEBUG] Synchronisation du token FCM dans les préférences"
          );
          preferences.fcmToken = tokenData.token;
          await this.saveNotificationPreferences(userId, {
            fcmToken: tokenData.token,
          });
        }
      } else {
        console.log(
          "🔔 [DEBUG] Aucun token FCM trouvé dans la collection fcmTokens"
        );
        // Si pas de token dans fcmTokens mais qu'il y en a un dans les préférences,
        // on le supprime des préférences
        if (preferences.fcmToken) {
          console.log(
            "🔔 [DEBUG] Suppression du token FCM des préférences (pas de token dans fcmTokens)"
          );
          delete preferences.fcmToken;
          await this.saveNotificationPreferences(userId, preferences);
        }
      }

      return preferences;
    } catch (error) {
      console.error(
        "🔔 [ERROR] Erreur lors de la récupération des préférences:",
        error
      );
      return null;
    }
  }

  // Ajouter/retirer un joueur de la liste de suivi
  async toggleFollowedPlayer(userId: string, playerId: string): Promise<void> {
    const prefs = await this.getNotificationPreferences(userId);
    const followedPlayers = prefs?.followedPlayers || [];

    let newFollowedPlayers: string[];
    if (followedPlayers.includes(playerId)) {
      newFollowedPlayers = followedPlayers.filter((id) => id !== playerId);
    } else {
      newFollowedPlayers = [...followedPlayers, playerId];
    }

    // Préserver tous les champs existants et ne modifier que followedPlayers
    const updatedPreferences: Partial<UserNotificationPreferences> = {
      followedPlayers: newFollowedPlayers,
    };

    // Préserver le fcmToken s'il existe
    if (prefs?.fcmToken) {
      updatedPreferences.fcmToken = prefs.fcmToken;
    }

    // Préserver les paramètres de notification s'ils existent
    if (prefs?.notificationTypes) {
      updatedPreferences.notificationTypes = prefs.notificationTypes;
    }

    // Préserver les notifications email s'ils existent
    if (prefs?.emailNotifications) {
      updatedPreferences.emailNotifications = prefs.emailNotifications;
    }

    await this.saveNotificationPreferences(userId, updatedPreferences);
  }

  // Récupérer tous les utilisateurs qui suivent un joueur spécifique
  async getUsersFollowingPlayer(playerId: string): Promise<string[]> {
    const prefsRef = collection(
      firestoreInstance,
      "userNotificationPreferences"
    );
    const q = query(
      prefsRef,
      where("followedPlayers", "array-contains", playerId)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => doc.data().userId);
  }

  // Vérifier si un utilisateur suit un joueur
  async isFollowingPlayer(userId: string, playerId: string): Promise<boolean> {
    const prefs = await this.getNotificationPreferences(userId);
    return prefs?.followedPlayers?.includes(playerId) || false;
  }

  // Supprimer le token FCM (déconnexion)
  async removeFCMToken(userId: string): Promise<void> {
    try {
      console.log(
        "🔔 [DEBUG] Suppression du token FCM pour l'utilisateur:",
        userId
      );

      // Supprimer le token de Firestore
      const tokenRef = doc(firestoreInstance, "fcmTokens", userId);
      await deleteDoc(tokenRef);
      console.log("🔔 [DEBUG] Token FCM supprimé de Firestore");

      // Supprimer le token côté client si possible
      try {
        const messaging = this.getMessaging();
        if (messaging) {
          // Note: Firebase Messaging n'a pas de méthode directe pour supprimer le token côté client
          // Le token sera automatiquement invalidé lors de la prochaine initialisation
          console.log(
            "🔔 [DEBUG] Token côté client sera invalidé lors de la prochaine initialisation"
          );
        }
      } catch (clientError) {
        console.log(
          "🔔 [DEBUG] Impossible de nettoyer le token côté client:",
          clientError
        );
      }

      console.log("🔔 [DEBUG] Suppression du token FCM terminée avec succès");
    } catch (error) {
      console.error(
        "🔔 [ERROR] Erreur lors de la suppression du token FCM:",
        error
      );
      throw error;
    }
  }
}

export const notificationService = new NotificationService();
